import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('lib:types');

import type {
  EffectivePersonalization,
  OrgPersonalizationSettings,
  PersonalizationMode,
  UserPersonalizationSettings,
} from './personalization';

/**
 * Computes the effective personalization settings based on active mode
 *
 * Mode 'org': Use pure org settings, ignore user customizations
 * Mode 'custom': Merge user settings with org settings (user takes priority)
 *
 * This is what gets injected into AI system prompts
 */
export function computeEffectivePersonalization(params: {
  activeMode: PersonalizationMode;
  userSettings: UserPersonalizationSettings;
  orgSettings: OrgPersonalizationSettings | null;
  user: { name?: string; email?: string; organizationId?: string } | null;
}): EffectivePersonalization {
  const { activeMode, userSettings, orgSettings, user } = params;

  // Fallback name if nothing is configured
  const fallbackName = user?.name || user?.email || 'User';
  const fallbackOrgName = user?.organizationId || 'Your Organization';

  // If no org settings exist yet, use minimal defaults
  if (!orgSettings) {
    logger.info('🎯 [PERSONALIZATION] No org settings available, using minimal defaults');
    return {
      name: userSettings.displayName || fallbackName,
      jobTitle: userSettings.jobTitle,
      organizationName: fallbackOrgName,
      specialty: userSettings.specialtyOverride || 'General',
      cptCodes: userSettings.additionalCptCodes || [],
      taxonomies: userSettings.additionalTaxonomies || [],
      customInstructions: userSettings.customInstructions,
      otherDetails: userSettings.otherDetails,
    };
  }

  // Mode: 'org' - Use pure org settings, ignore user customizations
  if (activeMode === 'org') {
    logger.info('🏢 [PERSONALIZATION] Active mode: org - using organization defaults');
    return {
      name: user?.name || fallbackName, // Use actual user name, not org name
      jobTitle: undefined, // No job title in org mode
      organizationName: orgSettings.organizationDisplayName,
      specialty: orgSettings.primarySpecialty,
      cptCodes: orgSettings.commonCptCodes,
      taxonomies: orgSettings.providerTaxonomies,
      customInstructions: undefined, // No custom instructions in org mode
      otherDetails: orgSettings.otherDetails, // Use org other details
    };
  }

  // Mode: 'custom' - Merge user settings with org settings (user takes priority)
  logger.info('👤 [PERSONALIZATION] Active mode: custom - merging user + org settings');
  logger.info(' User Settings:', JSON.stringify(userSettings, null, 2));
  logger.info(' Org Settings:', JSON.stringify(orgSettings, null, 2));

  const effectiveSettings: EffectivePersonalization = {
    // User's display name, or fallback to actual user name, then org name as last resort
    name: userSettings.displayName || user?.name || fallbackName,

    // User's job title (org settings don't have job titles)
    jobTitle: userSettings.jobTitle,

    // Organization name comes from org settings
    organizationName: orgSettings.organizationDisplayName || fallbackOrgName,

    // COMBINE specialty - user's specific specialty + org department specialty
    // This provides richer context: e.g., "Pediatric Gastro (Department: Gastroenterology)"
    specialty: (() => {
      const userSpec = userSettings.specialtyOverride;
      const orgSpec = orgSettings.primarySpecialty;

      if (userSpec && orgSpec && userSpec !== orgSpec) {
        // Both exist and are different - combine them
        return `${userSpec} (Department: ${orgSpec})`;
      }
      // Otherwise, use whichever exists or fallback
      return userSpec || orgSpec || 'General';
    })(),

    // Combine org CPT codes + user's additional codes (unique values only)
    cptCodes: Array.from(
      new Set([...(orgSettings.commonCptCodes || []), ...(userSettings.additionalCptCodes || [])])
    ),

    // Combine org taxonomies + user's additional taxonomies (unique values only)
    taxonomies: Array.from(
      new Set([
        ...(orgSettings.providerTaxonomies || []),
        ...(userSettings.additionalTaxonomies || []),
      ])
    ),

    // Custom instructions (user-level only)
    customInstructions: userSettings.customInstructions,

    // Combine user + org other details
    otherDetails: [userSettings.otherDetails, orgSettings.otherDetails]
      .filter(detail => detail && detail.trim())
      .join('\n\n'),
  };

  logger.info(
    '🎯 [PERSONALIZATION] Computed effective settings:',
    JSON.stringify(effectiveSettings, null, 2)
  );

  return effectiveSettings;
}

/**
 * Formats effective personalization into a system prompt section
 * This gets injected into AI conversations
 */
export function formatPersonalizationForPrompt(settings: EffectivePersonalization): string {
  const parts: string[] = [];

  // Lead with clear instructions about implicit usage
  parts.push(
    '--- USER CONTEXT (Background Information) ---\n\n' +
      'CRITICAL INSTRUCTION: The following information is background context about the user. ' +
      'Use it to inform your responses, tailor your language, and provide relevant suggestions. ' +
      'DO NOT explicitly list or repeat this information back to the user unless they specifically ask about their profile or settings.\n\n'
  );

  parts.push(`User: ${settings.name}`);

  if (settings.jobTitle) {
    parts.push(` (${settings.jobTitle})`);
  }

  parts.push(`\nOrganization: ${settings.organizationName}`);

  if (settings.specialty) {
    parts.push(`\nSpecialty: ${settings.specialty}`);
  }

  if (settings.cptCodes.length > 0) {
    parts.push(`\nCommonly uses CPT codes: ${settings.cptCodes.join(', ')}`);
  }

  if (settings.taxonomies.length > 0) {
    parts.push(`\nProvider taxonomies: ${settings.taxonomies.join(', ')}`);
  }

  if (settings.customInstructions) {
    parts.push(`\n\nSpecial Instructions:\n${settings.customInstructions}`);
  }

  if (settings.otherDetails) {
    parts.push(`\n\nAdditional Notes:\n${settings.otherDetails}`);
  }

  // Closing instruction - emphasize implicit usage
  parts.push(
    '\n\n--- END USER CONTEXT ---\n\n' +
      'Use this context naturally to provide personalized assistance. ' +
      "Only mention specific details (CPT codes, taxonomies, specialty) when they are directly relevant to the user's question. " +
      'Never start conversations by listing this information - treat it as background knowledge, not something to announce.'
  );

  return parts.join('');
}

/**
 * Checks if organization has completed personalization setup
 * Used to determine if onboarding flow should be shown
 */
export function hasCompletedOrgPersonalization(
  orgSettings: OrgPersonalizationSettings | null
): boolean {
  if (!orgSettings) {
    return false;
  }

  // Check if required fields are filled
  const hasRequiredFields =
    orgSettings.organizationDisplayName &&
    orgSettings.primarySpecialty &&
    orgSettings.commonCptCodes.length > 0;

  return !!hasRequiredFields;
}

/**
 * Checks if user has any custom settings configured
 */
export function hasUserCustomizations(userSettings: UserPersonalizationSettings): boolean {
  return !!(
    userSettings.displayName ||
    userSettings.jobTitle ||
    userSettings.specialtyOverride ||
    (userSettings.additionalCptCodes && userSettings.additionalCptCodes.length > 0) ||
    (userSettings.additionalTaxonomies && userSettings.additionalTaxonomies.length > 0)
  );
}
